import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { LayoutCompatibilityBadge } from './LayoutCompatibilityBadge';

import { formatDateTime, hasRole, useDeepCompareMemo, userRolesMap } from '@clatter/platform';
import { FloatingButton, ImagePreviewDialog } from '@clatter/ui';
import { DropdownSelect, IconButtonRounded, NgCard, NgCardContent, NgDivider } from '../../UI';
import { CircularLoader } from '../../../shared';
import { useItemThumbnail } from '../../../hooks';
import { authSelectors, viewStateSelectors } from '../../../store';
import { useAssetDownload } from '../../../hooks';
import { FolderManagerMimeTypes } from '../../../interfaces';
import CloseIcon from '@mui/icons-material/Close';
import { getLayoutInSelectedTheme } from './folderManagerGrid.shared';
import { formatFileSize, getIconForMimeType } from '../../../helpers';
import {
  useGetMetadataQuery,
  useUpdateAssetItemMetadataMutation,
  useUpdatePresentationItemMetadataMutation,
} from '../../../services';

interface DetailsViewProps {
  selectedItems: FolderManagerItem[];
  viewStateKey?: string;
  onClose: () => void;
  onAction?: (data: any) => void;
  stickyTop?: number;
}

const StyledProperties = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.large};
  width: 100%;
  flex: 1;

  img {
    width: 100%;
    height: auto;
    object-fit: cover;
  }

  dl {
    font-size: ${({ theme }) => theme.fontSize.default};
  }

  dt {
    margin-bottom: ${({ theme }) => theme.spacing.small};
    color: ${({ theme }) => theme.palette.darkGrey};
  }

  dd {
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: break-all;
    margin-left: 0;
    margin-bottom: ${({ theme }) => theme.spacing.large};
    font-size: ${({ theme }) => theme.fontSize.default};
    max-width: 100%;
    overflow: hidden;
  }

  dd.metadata-field {
    overflow: visible;
    word-break: normal;
  }
`;

const StyledDetailsWrapper = styled.aside<{ $stickyTop?: number }>`
  position: relative;
  width: 100%;
  max-height: 100%;
  overflow: visible;

  .info-text {
    font-size: ${({ theme }) => theme.fontSize.lg};
  }

  .details-card-sticky {
    position: sticky;
    top: ${({ $stickyTop, theme }) =>
      $stickyTop !== undefined ? `${$stickyTop}px` : `calc(${theme.spacing.medium} + 58px)`};
  }
`;

const StyledDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};

  width: 100%;
  height: 100%;

  .details-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid ${({ theme }) => theme.palette.grey};

    h3 {
      margin-bottom: 0;
    }
  }
`;

const StyledCenteredContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  min-height: 20vh;
`;

const StyledLoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 120px;
`;

const StyledDefaultThumbnail = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: ${({ theme }) => theme.radius.small};
  background-color: ${({ theme }) => theme.palette.grey};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 60px;
    height: 60px;
    opacity: 0.7;
  }
`;

interface DetailsRailThumbnailProps {
  item: FolderManagerItem;
  viewStateKey?: string;
}

const StyledThumbnailWrapper = styled.div`
  position: relative;
`;

const DetailsRailThumbnail: FC<DetailsRailThumbnailProps> = ({ item, viewStateKey }) => {
  const selectedThemeId = useSelector(viewStateSelectors.getSelectedThemeId(viewStateKey || ''));
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const { thumbnailUrl, isLoading, isFetching, isThemeChanging } = useItemThumbnail({
    itemId: item?.id,
    itemType: item?.type,
    thumbnailUrl: item?.thumbnailUrl,
    themeId: selectedThemeId,
  });

  if (isLoading || (isThemeChanging && isFetching)) {
    return (
      <StyledLoaderContainer>
        <CircularLoader size={30} />
      </StyledLoaderContainer>
    );
  }

  if (!thumbnailUrl) {
    return (
      <StyledDefaultThumbnail>
        {getIconForMimeType(item?.type, item?.isShared, item?.isSlideRepositoryFolder, item?.isStarterDeckFolder)}
      </StyledDefaultThumbnail>
    );
  }

  return (
    <StyledThumbnailWrapper>
      <FloatingButton $top={5} $right={6} onClick={() => setPreviewImage(thumbnailUrl)}>
        <VisibilityOutlinedIcon />
      </FloatingButton>
      <img src={thumbnailUrl} alt={item?.name || 'Asset Thumbnail'} />
      <ImagePreviewDialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageUrl={previewImage}
        alt={item?.name || 'Asset Preview'}
      />
    </StyledThumbnailWrapper>
  );
};

const StyledSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledMetadataControl = styled.div`
  width: 100%;

  input[type='number'],
  input[type='text'] {
    width: 100%;
    min-height: 40px;
    padding: 0 12px;
    border: 1px solid ${({ theme }) => theme.palette.grey};
    border-radius: ${({ theme }) => theme.radius.small};
    font-size: ${({ theme }) => theme.fontSize.default};
  }

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    margin: 0;
    accent-color: #1976d2;
    cursor: pointer;
  }

  label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
  }
`;

const normalizeMetadataPayload = (metadata: MetadataFieldPayload | null | undefined): AssetMetadataField[] => {
  if (!metadata) {
    return [];
  }

  return Object.entries(metadata).map(([key, value]) => ({
    key,
    value,
    allowed_values: [],
  }));
};

const getAssetMetadataSource = (data: FolderManagerItem<SlideFromApi> | FolderManagerItem) => {
  const slideAsset = (data.itemData as SlideFromApi | undefined)?.Asset;
  const metadata = data.metadata ?? slideAsset?.Metadata ?? null;
  const assetId =
    data.type === FolderManagerMimeTypes.SLIDE || data.type === FolderManagerMimeTypes.PRESENTATION
      ? null
      : Number(data.id);
  const presentationId = data.type === FolderManagerMimeTypes.PRESENTATION ? Number(data.id) : null;

  return {
    assetId: assetId != null && !Number.isNaN(assetId) ? assetId : null,
    presentationId: presentationId != null && !Number.isNaN(presentationId) ? presentationId : null,
    metadata,
  };
};

const getMetadataFields = (
  data: FolderManagerItem<SlideFromApi> | FolderManagerItem,
  metadataDefinitions: Array<{ key: string; allowed_values: string[] | null }>,
): AssetMetadataField[] => {
  const { metadata } = getAssetMetadataSource(data);
  const metadataByKey = new Map(normalizeMetadataPayload(metadata).map((field) => [field.key, field]));

  if (!Array.isArray(metadataDefinitions) || metadataDefinitions.length === 0) {
    return [];
  }

  return metadataDefinitions.map((definition) => {
    const existingField = metadataByKey.get(definition.key);

    return {
      key: definition.key,
      value: existingField?.value ?? null,
      allowed_values: definition.allowed_values || [],
    };
  });
};

const DetailsRailProperties = ({
  data,
  viewStateKey,
  onAction,
}: {
  data: FolderManagerItem<SlideFromApi> | FolderManagerItem;
  viewStateKey?: string;
  onAction?: (data: any) => void;
}) => {
  const activeUser = useSelector(authSelectors.getUserAuthState)?.user;
  const isAdmin = useDeepCompareMemo(() => hasRole(userRolesMap.ngAdmin, activeUser), [activeUser]);
  const isContentManager = useSelector(authSelectors.isContentManager);
  const selectedThemeId = useSelector(viewStateSelectors.getSelectedThemeId(viewStateKey || ''));
  const { data: metadataDefinitions = [] } = useGetMetadataQuery();
  const { downloadAssetVersionById } = useAssetDownload();
  const [updateAssetItemMetadata, { isLoading: isUpdatingMetadata }] = useUpdateAssetItemMetadataMutation();
  const [updatePresentationItemMetadata] = useUpdatePresentationItemMetadataMutation();
  const metadataSupported = data.type !== FolderManagerMimeTypes.SLIDE;
  const { assetId: metadataAssetId, presentationId: metadataPresentationId } = useMemo(
    () => getAssetMetadataSource(data),
    [data],
  );
  const metadataFields = useMemo(
    () => (metadataSupported ? getMetadataFields(data, metadataDefinitions) : []),
    [data, metadataDefinitions, metadataSupported],
  );
  const [localMetadataFields, setLocalMetadataFields] = useState<AssetMetadataField[]>(metadataFields);

  useEffect(() => {
    setLocalMetadataFields(metadataFields);
  }, [metadataFields]);

  const handleMetadataFieldChange = async (fieldKey: string, nextValue: AssetMetadataField['value']) => {
    const previousFields = localMetadataFields;
    const nextFields = previousFields.map((field) =>
      field.key === fieldKey
        ? {
            ...field,
            value: nextValue,
          }
        : field,
    );

    setLocalMetadataFields(nextFields);

    onAction?.({
      actionType: 'UPDATE_METADATA',
      payload: {
        item: data,
        metadata: nextFields,
        fieldKey,
        value: nextValue,
      },
    });

    if (!metadataSupported) {
      return;
    }

    try {
      if (metadataPresentationId) {
        await updatePresentationItemMetadata({
          presentationId: metadataPresentationId,
          data: nextFields,
        }).unwrap();
      } else if (metadataAssetId) {
        await updateAssetItemMetadata({
          assetId: metadataAssetId,
          data: nextFields,
        }).unwrap();
      }
    } catch (error) {
      setLocalMetadataFields(previousFields);

      onAction?.({
        actionType: 'UPDATE_METADATA_ERROR',
        payload: {
          item: data,
          metadata: previousFields,
          fieldKey,
          value: nextValue,
          error,
        },
      });
    }
  };

  const renderMetadataFieldControl = (field: AssetMetadataField) => {
    const options: IDropdownSelectOptionItem[] = [
      {
        label: 'None',
        value: 'None',
      },
      ...field.allowed_values.map((value) => ({
        label: value,
        value,
      })),
    ];

    return (
      <StyledMetadataControl>
        <DropdownSelect
          options={options}
          value={field.value == null ? options[0] : options.find((option) => option.value === field.value) || null}
          onChange={(option: IDropdownSelectOptionItem | null) =>
            handleMetadataFieldChange(field.key, !option || option.value === 'None' ? null : option.value)
          }
          placeholder="Select value"
          isSearchable={false}
          isDisabled={isUpdatingMetadata}
          isLoading={isUpdatingMetadata}
        />
      </StyledMetadataControl>
    );
  };

  const renderMetadataSection = () => {
    if (localMetadataFields.length === 0) {
      return null;
    }

    return (
      <>
        <NgDivider />

        {localMetadataFields.map((field) => (
          <Fragment key={field.key}>
            <dt>{field.key}</dt>
            <dd className="metadata-field">{renderMetadataFieldControl(field)}</dd>
          </Fragment>
        ))}
      </>
    );
  };

  const computed = useMemo(() => {
    if (data.type !== FolderManagerMimeTypes.SLIDE) {
      return null;
    }

    let presentation = data?.itemData || {};
    let slideData: {
      versionNumber: number | null;
      versionId: number | null;
      slideNumber: number | null;
      displayName: string;
      layoutName?: string | null;
      layoutSupportedInTheme: SlideLayoutSupportIndicator | null;
    } = {
      displayName: data.displayName || data.name,
      versionNumber: null,
      versionId: null,
      slideNumber: null,
      layoutName:
        (data as any)?.itemData?.slideData?.layoutName ||
        (data as any)?.itemData?.slideData?.LayoutName ||
        (data as any)?.itemData?.layoutName ||
        (data as any)?.itemData?.LayoutName ||
        null,
      layoutSupportedInTheme: getLayoutInSelectedTheme(data as FolderManagerItem, selectedThemeId) ?? null,
    };

    if (data?.itemData?.Asset) {
      const assetVersionId =
        data.itemData?.slideData?.AssetVersion?.ID ??
        data.itemData?.AssetVersion?.ID ??
        data.itemData?.Asset?.CurrentVersionID;
      const versionData: IAssetVersion | undefined = data?.itemData?.Asset?.Versions?.find(
        (version: IAssetVersion) => version.ID === assetVersionId,
      );

      const assetData: AssetFromApi = data.itemData?.Asset;

      if (versionData) {
        slideData.versionNumber = versionData.VersionNumber;
        slideData.versionId = versionData.ID;
      }

      presentation = {
        id: assetData.ID,
        displayName: assetData.DisplayName,
        fileSize: versionData?.FileSize,
        owner: {
          Email: assetData.Owner?.Email,
        },
        createdBy: {
          Email: assetData.CreatedBy?.Email,
        },
        createdAt: assetData.CreatedAt,
        updatedAt: assetData.UpdatedAt,
        trashedAt: assetData.TrashedAt,
      };
    } else if (data?.itemData?.currentVersion) {
      const itemData = data.itemData;
      const currentVersion = itemData.currentVersion;

      slideData.versionNumber = currentVersion.VersionNumber;
      slideData.versionId = currentVersion.ID;

      presentation = {
        id: itemData.id,
        displayName: itemData.name,
        fileSize: currentVersion.FileSize,
        owner: {
          Email: itemData.owner?.Email,
        },
        createdBy: {
          Email: itemData.createdBy?.Email,
        },
        createdAt: itemData.createdAt,
        updatedAt: itemData.updatedAt,
        trashedAt: itemData.trashedAt,
      };
    }

    return {
      presentation,
      slideData,
    };
  }, [data, selectedThemeId]);

  if (data.type === FolderManagerMimeTypes.SLIDE) {
    if (!computed) {
      return null;
    }

    const parentAssetId = computed.presentation?.id;

    return (
      <StyledProperties>
        <DetailsRailThumbnail item={data} viewStateKey={viewStateKey} />
        <>
          <dl>
            <dt>Name</dt>
            <dd>{computed?.slideData.displayName}</dd>

            {computed?.slideData?.slideNumber != null && (
              <>
                <dt>Slide Number</dt>
                <dd>{computed.slideData.slideNumber}</dd>
              </>
            )}

            <dt>Layout</dt>
            <dd>
              {(isAdmin || isContentManager) && (
                <LayoutCompatibilityBadge
                  layoutName={computed?.slideData?.layoutName}
                  supportLevel={computed?.slideData?.layoutSupportedInTheme ?? undefined}
                />
              )}
            </dd>

            <NgDivider />

            <StyledSectionHeader>
              <dt>Presentation</dt>
            </StyledSectionHeader>
            <dd></dd>

            <dt>Name</dt>
            <dd>{computed?.presentation?.displayName}</dd>

            <StyledSectionHeader>
              <dt>Version Number</dt>
              {parentAssetId && computed.slideData.versionId && (
                <IconButtonRounded
                  title="Download Version"
                  icon={<DownloadOutlinedIcon fontSize="small" />}
                  onClick={() =>
                    downloadAssetVersionById({
                      assetId: parentAssetId,
                      assetVersionId: computed.slideData.versionId!,
                    })
                  }
                  testId="details-rail-download-version"
                  materialIcon
                  clear
                />
              )}
            </StyledSectionHeader>
            <dd>{computed?.slideData?.versionNumber}</dd>

            <dt>Owner</dt>
            <dd>{computed.presentation?.owner?.Email || '-'}</dd>

            <dt>Creator</dt>
            <dd>{computed?.presentation?.createdBy?.Email || '-'}</dd>

            <dt>File Size</dt>
            <dd>{computed?.presentation?.fileSize ? formatFileSize(computed?.presentation.fileSize) : '-'}</dd>

            <dt>Created</dt>
            <dd>{formatDateTime(computed?.presentation?.createdAt)}</dd>

            <dt>Modified</dt>
            <dd>{computed?.presentation?.updatedAt ? formatDateTime(computed?.presentation?.updatedAt) : '-'}</dd>

            {computed?.presentation?.trashedAt && (
              <>
                <dt>Deleted</dt>
                <dd>{formatDateTime(computed?.presentation?.trashedAt)}</dd>
              </>
            )}

            {renderMetadataSection()}
          </dl>
        </>
      </StyledProperties>
    );
  }

  return (
    <StyledProperties>
      <DetailsRailThumbnail item={data} viewStateKey={viewStateKey} />
      <dl>
        <dt>Name</dt>
        <dd>{data?.name || data?.displayName}</dd>

        <dt>Owner</dt>
        <dd>{data?.owner?.Email || '-'}</dd>

        <dt>Creator</dt>
        <dd>{data?.createdBy?.Email || '-'}</dd>

        <dt>File Size</dt>
        <dd>{data?.fileSize ? formatFileSize(data.fileSize) : '-'}</dd>

        <dt>Created</dt>
        <dd>{formatDateTime(data?.createdAt)}</dd>

        <dt>Modified</dt>
        <dd>{data?.updatedAt ? formatDateTime(data?.updatedAt) : '-'}</dd>

        {data?.trashedAt && (
          <>
            <dt>Deleted</dt>
            <dd>{formatDateTime(data?.trashedAt)}</dd>
          </>
        )}

        {renderMetadataSection()}
      </dl>
    </StyledProperties>
  );
};

export const DetailsRail: FC<DetailsViewProps> = ({
  selectedItems = [],
  onClose,
  onAction,
  stickyTop,
  viewStateKey,
}) => (
  <StyledDetailsWrapper $stickyTop={stickyTop}>
    <div className="details-card-sticky">
      <NgCard>
        <NgCardContent>
          {selectedItems?.length <= 0 && (
            <StyledCenteredContainer>
              <p className="info-text">No selected item.</p>
            </StyledCenteredContainer>
          )}

          {selectedItems?.length > 1 && (
            <StyledCenteredContainer>
              <p className="info-text">{selectedItems.length} selected.</p>
            </StyledCenteredContainer>
          )}

          {selectedItems?.length === 1 && (
            <StyledDetails>
              <div className="details-header">
                <h3>Details</h3>

                <IconButtonRounded title="Close" icon={<CloseIcon />} onClick={onClose} filled clear />
              </div>

              <DetailsRailProperties data={selectedItems[0]} viewStateKey={viewStateKey} onAction={onAction} />
            </StyledDetails>
          )}
        </NgCardContent>
      </NgCard>
    </div>
  </StyledDetailsWrapper>
);
